import { createMutation, createQuery, createSuspenseQuery, makeQueryKey } from "@steto-care/steto-query";

import { AlertsRepository } from "@/io/repository/AlertsRepository.ts";
import { CreateAlertConfig, EditAlertConfig } from "@/models/AlertModel.ts";
import { PractitionerQueries } from "@/queries/PractitionerQueries.ts";

export class AlertsQueries {
  constructor(
    private readonly practitioners: PractitionerQueries = new PractitionerQueries(),
    private readonly alertsRepository: AlertsRepository = new AlertsRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useAlertConfigList = createQuery(
    "alert-config-list",
    variables => [variables],
    async (patientId: string) => await this.alertsRepository.getAlertConfigList(patientId),
  );

  useListPatientAlerts = createSuspenseQuery(
    "patient-alerts",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.alertsRepository.getAlertList(patientId),
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  useCreateAlertConfig = createMutation(
    "create-alert-config",
    async (data: { patientId: string; alertConfig: CreateAlertConfig }) =>
      this.alertsRepository.createAlertConfig(data.patientId, data.alertConfig),
    {
      onSuccess: (_, variables, { client }) => {
        this.useAlertConfigList.invalidate(client, variables.patientId);
      },
    },
  );

  useEditAlertConfig = createMutation(
    "edit-alert-config",
    async (data: { patientId: string; alertConfig: EditAlertConfig }) =>
      this.alertsRepository.editAlertConfig(data.patientId, data.alertConfig),
    {
      onSuccess: (_, variables, { client }) => {
        this.useAlertConfigList.invalidate(client, variables.patientId);
      },
    },
  );

  useDeleteAlertConfig = createMutation(
    "delete-alert-config",
    async (data: { patientId: string; alertConfigId: string }) =>
      this.alertsRepository.deleteAlertConfig(data.patientId, data.alertConfigId),
    {
      onSuccess: (_, variables, { client }) => {
        this.useAlertConfigList.invalidate(client, variables.patientId);
      },
    },
  );

  useDismissAlert = createMutation(
    "dismiss-alert",
    async (data: { patientId: string; alertId: string }) =>
      this.alertsRepository.dismissAlert(data.patientId, data.alertId),
    {
      onSuccess: (_, variables, { client }) => {
        this.useListPatientAlerts.invalidate(client, variables.patientId);
        this.practitioners.useDoctorPatientList.invalidate(client, {});
        this.practitioners.useNursePatientList.invalidate(client, {});
      },
    },
  );
}
